import React, { useEffect, useId, useRef } from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface CheckboxInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>, ForwardedHTMLProps {
  label?: string;
  description?: string;
  indeterminate?: boolean;
  error?: boolean;
  size?: ComponentSize;
  labelPosition?: 'left' | 'right';
  className?: string;
}

const sizeClasses: Record<ComponentSize, { checkbox: string; label: string; gap: string; wrapperHeight: string }> = {
  xs: { checkbox: 'w-3 h-3', label: 'text-xs', gap: 'gap-1.5', wrapperHeight: 'h-4' },
  sm: { checkbox: 'w-3.5 h-3.5', label: 'text-sm', gap: 'gap-2', wrapperHeight: 'h-5' },
  md: { checkbox: 'w-4 h-4', label: 'text-sm', gap: 'gap-2', wrapperHeight: 'h-5' },
  lg: { checkbox: 'w-5 h-5', label: 'text-base', gap: 'gap-2.5', wrapperHeight: 'h-6' },
  xl: { checkbox: 'w-6 h-6', label: 'text-lg', gap: 'gap-3', wrapperHeight: 'h-7' },
};

export const CheckboxInput = React.forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    {
      label,
      description,
      indeterminate = false,
      error = false,
      size = 'md',
      labelPosition = 'right',
      disabled,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const internalRef = useRef<HTMLInputElement>(null);
    const checkboxRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
    const uniqueId = useId();
    const inputId = id || uniqueId;
    const descriptionId = description ? `${inputId}-desc` : undefined;

    const sizeConfig = sizeClasses[size];

    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, checkboxRef]);

    const checkboxElement = (
      <input
        ref={checkboxRef}
        id={inputId}
        type="checkbox"
        disabled={disabled}
        aria-describedby={descriptionId}
        aria-invalid={error}
        className={cn(
          sizeConfig.checkbox,
          'rounded',
          'border',
          error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary',
          'text-primary',
          'focus:ring-2',
          'focus:ring-offset-2',
          'dark:focus:ring-offset-slate-900',
          'transition-colors',
          'cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          'accent-primary'
        )}
        {...forwardedProps}
        {...props}
      />
    );

    const labelElement = (label || description) && (
      <span className="flex flex-col">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              sizeConfig.label,
              'font-medium',
              tef.colors.foreground,
              disabled && 'opacity-50',
              'cursor-pointer',
              disabled && 'cursor-not-allowed'
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <span id={descriptionId} className={cn('text-xs mt-0.5', tef.colors.foregroundTertiary)}>
            {description}
          </span>
        )}
      </span>
    );

    return (
      <span
        className={cn(
          'inline-flex items-start',
          sizeConfig.gap,
          labelPosition === 'left' && 'flex-row-reverse',
          className
        )}
      >
        <span className={cn('flex items-center', sizeConfig.wrapperHeight)}>{checkboxElement}</span>
        {labelElement}
      </span>
    );
  }
);

CheckboxInput.displayName = 'CheckboxInput';

export default CheckboxInput;
